import React, { useState, useEffect, useMemo } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material"; 
import { useNavigate } from "react-router-dom";
import "maplibre-gl/dist/maplibre-gl.css";

import PageHeader from "../../components/PageHeader";
import Socialmedia from "markup/components/Socialmedia";
import LinesDecor from "markup/components/LinesDecor";

import SearchControls from "./components/SearchControls";
import MobileStationPanel from "./components/MobileStationPanel";
import StationList from "./components/StationList"; 
import MapStyles from "./components/MapStyles";
import StationPopup from "./components/StationPopup";

import { COLORS, geocodeLocation } from "./components/constants";
import useStationMap from "./hooks/useStationMap";
import { useStations } from "../../../hooks/useStations"; 

const STORAGE_KEY = "stationFinderState";

const getSavedState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const saveState = (state) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; 
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

export default function FindStations() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); 

  const savedState = useMemo(() => getSavedState(), []);

  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || "");
  const [radius, setRadius] = useState(savedState?.radius || 100);
  const [selectedStation, setSelectedStation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);
  const [searchedLocation, setSearchedLocation] = useState(savedState?.searchedLocation || null);

  const { data: allStations = [], isLoading: stationsLoading } = useStations();

  const stations = useMemo(() => {
    if (!searchedLocation || !allStations.length) return [];
    const withDistance = allStations
      .filter((s) => !isNaN(parseFloat(s.lat)) && !isNaN(parseFloat(s.lon)))
      .map((s) => {
        const dist = calculateDistance(
          searchedLocation.lat,
          searchedLocation.lon,
          parseFloat(s.lat),
          parseFloat(s.lon)
        );
        return { ...s, distance: dist };
      });

    return withDistance
      .filter((s) => s.distance <= radius)
      .sort((a, b) => a.distance - b.distance);
  }, [searchedLocation, allStations, radius]);

  const { mapContainer, popupContainer, popupStation, closePopup } = useStationMap({
    stations,
    searchedLocation,
    radius,
    selectedStation,
    setSelectedStation,
  });

  useEffect(() => {
    if (searchedLocation) {
      saveState({ searchQuery, radius, searchedLocation });
      setError(stations.length === 0 ? `No stations found within ${radius}km` : null);
    }
  }, [searchQuery, radius, searchedLocation, stations.length]);

  const handleSearch = async (queryPassed) => {
    const query = typeof queryPassed === "string" ? queryPassed : searchQuery;
    setSearchQuery(query);

    if (!query?.trim()) { setError("Please enter a location"); return; }
    
    setLoading(true);
    setSelectedStation(null); 

    try {
      const geocoded = await geocodeLocation(query);
      if (!geocoded) {
        setError("Location not found");
      } else {
        setSearchedLocation(geocoded);
        setError(null);
      }
    } catch (err) { 
      setError("Search failed"); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) { setError("Geolocation not supported"); return; }
    setLocating(true);
    setSelectedStation(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSearchedLocation({ 
          lat: pos.coords.latitude, 
          lon: pos.coords.longitude, 
          name: "Your Location" 
        });
        setSearchQuery("");
        setLocating(false);
        setError(null);
      },
      () => { setError("Unable to get location"); setLocating(false); }
    );
  };

  const handleClearLocation = () => {
    setSearchedLocation(null);
    setSearchQuery("");
    setSelectedStation(null);
    setError(null);
  };

  const isLoading = loading || stationsLoading;

  return (
    <>
      <PageHeader title="STATION FINDER" />
      
      <SearchControls
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        radius={radius} setRadius={setRadius}
        loading={isLoading} locating={locating}
        error={error} searchedLocation={searchedLocation}
        onSearch={handleSearch} 
        onUseMyLocation={handleUseMyLocation}
        onClearLocation={handleClearLocation} 
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, 
          // Optimized Desktop Height: 100vh - 300px
          height: { xs: "calc(100dvh - 160px)", md: "calc(100vh - 300px)" },
          minHeight: { xs: 500, md: 550 }, 
          mb: { xs: 0, md: 4 }, 
          mx: { xs: 0, md: 2, lg: 3 },
          borderRadius: { xs: 0, md: "20px" },
          overflow: "hidden", 
          boxShadow: { xs: "none", md: "0 8px 40px rgba(0, 0, 0, 0.08)" },
          border: { xs: "none", md: `1px solid ${COLORS.border}` },
          position: "relative",
          bgcolor: "#fff"
        }}
      >
        {/* PANEL AREA (The List) */}
        <Box sx={{ 
          flex: { xs: 1, md: 1 }, 
          minWidth: { md: "360px", lg: "420px" },
          height: { xs: "auto", md: "100%" }, 
          order: { xs: 2, md: 1 }, 
          position: "relative",
          zIndex: 2,
          overflow: "hidden" 
        }}>
          {isMobile ? (
             <MobileStationPanel
                stations={stations}
                selectedStation={selectedStation}
                setSelectedStation={setSelectedStation}
                searchedLocation={searchedLocation}
                isLoading={isLoading}
                allStations={allStations}
             />
          ) : (
             <StationList
                stations={stations}
                selectedStation={selectedStation}
                setSelectedStation={setSelectedStation}
                searchedLocation={searchedLocation}
                loading={isLoading}
                error={error}
                allStations={allStations}
             />
          )}
        </Box>

        {/* MAP AREA */}
        <Box sx={{ 
          flex: { xs: "none", md: 2, lg: 3 }, 
          height: { xs: "35%", md: "100%" }, 
          order: { xs: 1, md: 2 },
          zIndex: 1, 
          position: "relative",
          borderBottom: { xs: `1px solid ${COLORS.border}`, md: "none" }
        }}>
          <Box ref={mapContainer} sx={{ position: "absolute", inset: 0 }} />
        </Box>
      </Box>

      <Socialmedia />
      <Box sx={{ position: "relative", width: "100%" }}>

         <LinesDecor greenMt={{ lg: -6, md: -5, xs: -3 }} blueMt={{ lg: -2, md: -2 }} />
      </Box>
      
      <MapStyles />
      <StationPopup 
        station={popupStation} 
        container={popupContainer} 
        onClose={closePopup} 
        allStations={allStations} 
      />
    </>
  );
}